import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import { useUserProfile } from '../Auth/UserProfileContext';
import OrganiserSidebar from '../../OrganiserProfiles/OrganiserComponents/OrganiserSidebar';
import TalentSidebar from '../../TalentProfiles/TalentProfileComponents/TalentSidebar';
import ProfileActiveBadge from '../Profile/ProfileActiveBadge';
import DashboardBg from "../../assets/Backgrounds/DashboardDesktopBackground.jpg";
import BandCrowdBg from '../../assets/Images/bandcrowd2.jpg';

const UniversalProfileLayout = ({ 
  children, 
  title = "", 
  showTitle = true,
  requireAuth = true,
  allowedUserTypes = ['Organiser', 'Talent'], // Which user types can access
  customStyles = {},
  containerPadding = "2rem",
  minContentHeight = "calc(100vh - 10rem)" // Default min height for dashboard components
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useUserProfile();

  // Check if we're on TalentRiders page
  const isTalentRidersPage = location.pathname.includes('talent-riders');
  
  // State management
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [sidebarScaleFactor, setSidebarScaleFactor] = useState(1);
  const [contentScaleFactor, setContentScaleFactor] = useState(1);
  const [userType, setUserType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Determine user type from profile
  useEffect(() => {
    if (profile) {
      if (profile?.userType === 'Organiser') {
        setUserType('Organiser');
      } else if (profile?.userType === 'Talent') {
        setUserType('Talent');
      }
    }
  }, [profile]);
  
  // Authentication and authorization check
  useEffect(() => {
    const checkAuth = async () => {
      // Wait for auth to load
      if (authLoading || profileLoading) {
        return;
      }
      
      // Check if user is authenticated
      if (requireAuth && !currentUser) {
        navigate('/login');
        return;
      }
      
      // Check if user type is allowed
      if (userType && !allowedUserTypes.includes(userType)) {
        // Redirect to appropriate dashboard
        if (userType === 'Organiser') {
          navigate(`/organiser-dashboard/${currentUser.uid}`);
        } else if (userType === 'Talent') {
          navigate(`/talent-dashboard/${currentUser.uid}`);
        } else {
          navigate('/');
        }
        return;
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
  }, [authLoading, profileLoading, currentUser, userType, requireAuth, allowedUserTypes, navigate]);
  
  // Handle responsive sizing and dynamic height adjustment
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 1200);
      setWindowWidth(width);
      
      // Calculate scale factors for both sidebar and content
      if (width >= 1900) {
        setSidebarScaleFactor(1);
        setContentScaleFactor(1);
      } else if (width <= 1200) {
        setSidebarScaleFactor(0.75);
        setContentScaleFactor(0.85); // Content scales less aggressively
      } else {
        // Linear interpolation between min and max for widths 1200-1900
        const sidebarScale = 0.75 + ((width - 1200) / 700) * 0.25;
        const contentScale = 0.85 + ((width - 1200) / 700) * 0.15;
        setSidebarScaleFactor(sidebarScale);
        setContentScaleFactor(contentScale);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  
  // Loading state
  if (authLoading || profileLoading || isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#144874',
        color: 'white',
        fontSize: '1.5rem',
        fontFamily: 'Montserrat, sans-serif'
      }}>
        Loading...
      </div>
    );
  }
  
  // If no user or profile, don't render
  if (!currentUser || !profile) {
    return null;
  }
  
  // Select appropriate sidebar component
  const SidebarComponent = userType === 'Organiser' ? OrganiserSidebar : TalentSidebar;
  
  // Main layout styles with content scaling
  const styles = {
    wrapper: {
      position: 'relative',
      paddingTop: 0, // No top padding - handled by containers
      minHeight: 'auto', // Remove height constraints
      paddingBottom: '2rem',
      display: 'flex',
      flexDirection: 'column',
      backgroundImage: `url(${DashboardBg})`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed",
      backgroundPosition: "top center",
      ...customStyles.wrapper
    },
    desktopContainer: {
      display: 'flex',
      minHeight: 'auto', // Remove height constraints
      flexDirection: 'row',
      // Fixed positioning: 7rem from top, 1rem from right edge
      paddingLeft: '0', // No left padding - handled by sidebar margin
      paddingRight: '1rem', // 1rem from right edge
      paddingBottom: windowWidth > 1650 ? '0.5rem' : windowWidth > 700 ? '0.25rem' : '2px',
      paddingTop: '8rem', // 12rem from top of screen (7rem + 5rem adjustment)
      boxSizing: 'border-box',
      backgroundColor: 'transparent',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      ...customStyles.desktopContainer
    },
    sidebarContainer: {
      flex: `0 0 ${284 * sidebarScaleFactor}px`, // Reduced by 16px (1rem) to match sidebar
      backgroundColor: 'transparent',
      transition: 'flex 0.3s ease',
      // 1rem gap from sidebar to content
      marginRight: '1rem',
      ...customStyles.sidebarContainer
    },
    mainContainer: {
      flex: 1, // Restore width flexibility while allowing height expansion
      minHeight: 'auto', // Remove height constraints but keep width
      // Minimal padding below 700px, adjusted for screen widths
      paddingLeft: windowWidth > 1650 ? `${parseFloat(containerPadding) * contentScaleFactor}rem` : 
                   windowWidth > 700 ? '0.5rem' : '2px',
      paddingRight: windowWidth > 1650 ? `${parseFloat(containerPadding) * contentScaleFactor}rem` : 
                    windowWidth > 700 ? '0.5rem' : '2px',
      paddingBottom: windowWidth > 1650 ? `${parseFloat(containerPadding) * contentScaleFactor}rem` : 
                     windowWidth > 700 ? '0.5rem' : '2px',
      fontFamily: "Montserrat, sans-serif",
      overflow: "hidden",
      boxSizing: "border-box",
      transition: 'all 0.3s ease',
      width: '100%',
      maxWidth: '100%',
      ...customStyles.mainContainer
    },
    contentBox: {
      maxWidth: windowWidth > 1650 ? '2000px' : '100%',
      width: '100%',
      margin: '0 auto', // Center the content box
      backgroundColor: "white",
      borderRadius: '15px', // 15px border radius for rounded edges
      overflow: 'hidden', // Hide overflow for border-radius clipping only
      color: "#144874",
      minHeight: isTalentRidersPage ? 'calc(85vh + 10rem)' : '85vh', // Add 10rem for TalentRiders
      maxHeight: isTalentRidersPage ? 'calc(85vh + 10rem)' : '85vh', // Add 10rem for TalentRiders
      height: isTalentRidersPage ? 'calc(85vh + 10rem)' : '85vh', // Add 10rem for TalentRiders
      display: "flex",
      flexDirection: "column",
      fontSize: `${16 * contentScaleFactor}px`, // Base font size scaling
      boxShadow: 'none',
      position: 'relative',
      boxSizing: 'border-box',
      ...customStyles.contentBox
    },
    titleSection: {
      backgroundImage: `url(${BandCrowdBg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: 'white',
      padding: `${1 * contentScaleFactor}rem ${1.5 * contentScaleFactor}rem`,
      boxShadow: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: title?.mobileSecondLine ? '90px' : '70px', // Increased for two-line layout
      maxHeight: title?.mobileSecondLine ? '110px' : '85px',
      width: '100%',
      margin: '0',
      overflow: 'hidden',
      ...customStyles.titleSection
    },
    title: {
      fontSize: `${1.8 * contentScaleFactor}rem`,
      color: 'white',
      fontWeight: 'bold',
      fontFamily: 'Pondar, sans-serif',
      textAlign: 'center',
      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
      margin: 0,
      ...customStyles.title
    },
    contentArea: {
      flex: 1, // Take remaining space
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto', // Single scroll point for all content
      overflowX: 'hidden', // No horizontal scrolling
      // Minimal padding below 700px, extra bottom padding for scroll clearance
      padding: windowWidth > 700 ? `${1 * contentScaleFactor}rem` : '2px',
      paddingBottom: windowWidth > 700 ? `${3 * contentScaleFactor}rem` : '2rem', // Extra bottom padding
      boxSizing: 'border-box',
      ...customStyles.contentArea
    },
    mobileContainer: {
      width: '100%',
      height: '100%',
      backgroundColor: 'white',
      paddingLeft: '1rem',
      paddingRight: '1rem', 
      paddingBottom: '1rem',
      paddingTop: '5rem', // Consistent header clearance (5.5rem + 5rem adjustment)
      boxSizing: 'border-box',
      ...customStyles.mobileContainer
    }
  };
  
  // Mobile layout
  if (isMobile) {
    return (
      <div style={styles.wrapper}>
        {/* Render sidebar component for mobile menu - it will only show the FloatingMenuTab */}
        <SidebarComponent currentUser={currentUser} scaleFactor={sidebarScaleFactor} />
        
        {/* Mobile Content with white container */}
        <div style={{
          // Minimal padding on mobile below 700px
          paddingLeft: windowWidth > 700 ? '1rem' : '2px',
          paddingRight: windowWidth > 700 ? '1rem' : '2px',
          paddingBottom: windowWidth > 700 ? '1rem' : '2px',
          paddingTop: '5rem', // Clear the fixed header on mobile (5.5rem + 5rem adjustment)
          minHeight: 'auto', // Remove height constraints
          boxSizing: 'border-box'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 0, // No rounded edges
            overflow: 'hidden',
            minHeight: '90vh', // Minimum height for visual consistency
            height: 'auto', // Allow natural content height
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'none',
            position: 'relative'
          }}>
            {showTitle && title && (
              <div style={{
                backgroundImage: `url(${BandCrowdBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                color: 'white',
                padding: `${0.8 * contentScaleFactor}rem ${1 * contentScaleFactor}rem`,
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: title?.mobileSecondLine ? '80px' : '60px', // Increased height when two lines
                maxHeight: title?.mobileSecondLine ? '95px' : '75px',
                width: '100%',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {typeof title === 'object' && title.mobileFirstLine ? (
                  // Structured title with mobile line breaks
                  <div style={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '2px'
                  }}>
                    <div style={{
                      fontSize: `${1.2 * contentScaleFactor}rem`,
                      color: 'white',
                      fontWeight: 'bold',
                      fontFamily: 'Pondar, sans-serif',
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                      margin: 0,
                      lineHeight: '1.1'
                    }}>
                      {title.mobileFirstLine}
                    </div>
                    {title.mobileSecondLine && (
                      <div style={{
                        fontSize: `${1.6 * contentScaleFactor}rem`,
                        color: 'white',
                        fontWeight: 'bold',
                        fontFamily: 'Pondar, sans-serif',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                        margin: 0,
                        lineHeight: '1.1'
                      }}>
                        {title.mobileSecondLine}
                      </div>
                    )}
                  </div>
                ) : (
                  // Fallback for string titles
                  <h1 style={{
                    fontSize: `${1.5 * contentScaleFactor}rem`,
                    color: 'white',
                    fontWeight: 'bold',
                    fontFamily: 'Pondar, sans-serif',
                    textAlign: 'center',
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                    margin: 0
                  }}>
                    {typeof title === 'object' ? title.text : title}
                  </h1>
                )}

                {/* ProfileActiveBadge positioned at top right for mobile */}
                <div style={{
                  position: 'absolute',
                  right: '5px',
                  top: '5px',
                  zIndex: 10,
                  transform: 'scale(0.45)',
                  transformOrigin: 'top right'
                }}>
                  <ProfileActiveBadge
                    position="static"
                    top="0"
                    right="0"
                    transform="none"
                    zIndex={1}
                  />
                </div>
              </div>
            )}
            <div style={{
              flex: 1, // Take remaining space
              // Minimal padding below 700px
              paddingLeft: windowWidth > 700 ? `${1 * contentScaleFactor}rem` : '2px',
              paddingRight: windowWidth > 700 ? `${1 * contentScaleFactor}rem` : '2px',
              paddingBottom: windowWidth > 700 ? `${1 * contentScaleFactor}rem` : '2px',
              overflowY: 'auto', // Single scroll point for mobile content
              overflowX: 'hidden'
            }}>
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Desktop layout
  return (
    <div style={styles.wrapper}>
      <style>{`
        .universal-profile-content::-webkit-scrollbar {
          width: 8px;
        }
        .universal-profile-content::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .universal-profile-content::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        .universal-profile-content::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
      <div style={styles.desktopContainer}>
        {/* Sidebar */}
        <div style={styles.sidebarContainer}>
          <SidebarComponent currentUser={currentUser} scaleFactor={sidebarScaleFactor} />
        </div>
        
        {/* Main Content Area */}
        <div style={styles.mainContainer}>
          <div className="universal-content-box" style={styles.contentBox}>
            {/* Title Section */}
            {showTitle && title && (
              <div style={{...styles.titleSection, position: 'relative'}}>
                {typeof title === 'object' && title.mobileFirstLine ? (
                  // Structured title with line breaks for all viewports
                  <div style={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '0.2rem'
                  }}>
                    <div style={{
                      fontSize: `${1.5 * contentScaleFactor}rem`,
                      color: 'white',
                      fontWeight: 'bold',
                      fontFamily: 'Pondar, sans-serif',
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                      margin: 0,
                      lineHeight: '1.1'
                    }}>
                      {title.mobileFirstLine}
                    </div>
                    {title.mobileSecondLine && (
                      <div style={{
                        fontSize: `${2.2 * contentScaleFactor}rem`,
                        color: 'white',
                        fontWeight: 'bold',
                        fontFamily: 'Pondar, sans-serif',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                        margin: 0,
                        lineHeight: '1.1'
                      }}>
                        {title.mobileSecondLine}
                      </div>
                    )}
                  </div>
                ) : (
                  // Fallback for string titles
                  <h1 style={styles.title}>
                    {typeof title === 'object' ? title.text : title}
                  </h1>
                )}
                {/* ProfileActiveBadge positioned on far right */}
                <div style={{
                  position: 'absolute',
                  right: `${1.5 * contentScaleFactor}rem`,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10
                }}>
                  <ProfileActiveBadge
                    position="static"
                    top="auto"
                    right="auto"
                    transform="scale(0.85)"
                    zIndex={10}
                  />
                </div>
              </div>
            )}
            
            {/* Content Area */}
            <div className="universal-profile-content" style={styles.contentArea}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversalProfileLayout;